import React from 'react'
import { toast } from 'react-toastify'
import { Link } from 'react-router-dom'
import { useHistory } from 'react-router'

import useCart from 'store/CartProvider'
import useProduct from 'store/SingleProductProvider'
import CustomCart from 'components/Icons/customCart'
import { useWishlistContext } from 'store/WishlistContext'
import { percentageSaleCalculator, isInSale } from 'consts'
import CustomWishlist from 'components/Icons/customWishlist'
import { ProductImage, WishlistButton } from './ProductItem.style'
import { onProductAddToCartEvent } from 'services/conversionevents'
import ProductTag from 'pages/productDetailPage/components/ProductTag'

const ProductItem = props => {
  const history = useHistory()
  const { addToCart, payMethod } = useCart()
  const { addToWishlist } = useWishlistContext()
  const { product, chosenVariants } = useProduct()

  const item = props.item || {}
  const isOutOfStock = item.stock_status === 0

  const itemAddedToCart = status => {
    let shouldRedirect = status === 'buy'

    setTimeout(() => {
      addToCart(item.sku, chosenVariants, true, shouldRedirect).then(() => {
        onProductAddToCartEvent(product, {
          payMethod: payMethod,
        })
        if (shouldRedirect) {
          history.push('/purchase')
        }
      })
    }, 300)
  }

  const handleCartClick = e => {
    e.preventDefault()
    if (isOutOfStock) return
    itemAddedToCart('cart')
    toast.success('პროდუქტი დაემატა კალათაში')
  }

  const handleWishlistClick = e => {
    e.preventDefault()
    addToWishlist(item.sku, {})
  }

  return (
    <Link to={item.slug ? `/product/${item.slug}` : item.url ? item.url : '/'}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-md transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-lg border-t-2 border-[#f9fafb]">
        <div className="relative h-[140px] md:h-[180px]">
          {isInSale(item.old_price, item.price) && item.stock_status !== 0 ? (
            <div className="absolute top-2 left-2 md:top-3 md:left-3 bg-[#dc2626] text-white px-[7px] py-[2px] rounded-full text-xs md:text-[13px] font-semibold z-10">
              -{percentageSaleCalculator(item.old_price, item.price)}%
            </div>
          ) : null}

          <div className="absolute top-2 right-2 md:top-3 md:right-3 w-auto h-auto">
            <ProductTag
              type="card"
              iconType={item.icon}
              className="absolute top-0 right-0 h-6 w-6 md:h-8 md:w-8 text-[11px] md:text-[13px]"
            />

            <WishlistButton
              className={`group absolute right-0 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center z-10 text-[#2c3e50] transition-all duration-200 hover:scale-105 shadow-[0_2px_8px_rgba(0,0,0,0.1)] ${
                item.icon ? 'top-8 md:top-10' : 'top-0'
              }`}
              type="button"
              onClick={handleWishlistClick}
            >
              <CustomWishlist
                className="text-slate-300 group-hover:text-slate-600 transition-colors duration-300"
                size={15}
              />
            </WishlistButton>
          </div>

          <ProductImage
            src={item.image}
            alt={item.name}
            effect="blur"
            wrapperClassName="w-full h-full"
            isOutOfStock={isOutOfStock}
            placeholderSrc={item.image}
          />
        </div>

        <div className="p-3 md:p-5">
          <div className="mb-1 min-h-[18px]">
            {(item.stock_status === 1 || item.stock_status === 3) && (
              <div className="flex gap-1 items-center text-[#5fb20a] text-[11px] md:text-[13px] font-semibold">
                <div className="w-1.5 h-1.5 bg-[#5fb20a] rounded-full"></div>
                მარაგშია
              </div>
            )}
            {item.stock_status === 2 && (
              <div className="flex gap-1 items-center text-[#d4a418] text-[11px] md:text-[13px] font-semibold">
                <div className="w-1.5 h-1.5 bg-[#d4a418] rounded-full"></div>
                გადასამოწმებელია
              </div>
            )}
            {item.stock_status === 0 && (
              <div className="flex gap-1 items-center text-[#2c3e50] text-[11px] md:text-[13px] font-semibold">
                <div className="w-1.5 h-1.5 bg-[#2c3e50] rounded-full"></div>
                არ არის მარაგში
              </div>
            )}
          </div>

          <h3 className="text-[13px] md:text-[15px] font-semibold text-[#2c3e50] line-clamp-2 leading-tight h-[2.6em] md:h-[2.5em]">
            {item.name}
          </h3>

          <div
            className="flex items-center gap-1.5 md:gap-2 min-h-[34px] min-[1199px]:min-h-[40px]"
            itemProp="offers"
            itemScope
            itemType="https://schema.org/Offer"
          >
            {!isOutOfStock && item.price && (
              <>
                <span
                  className="text-[15px] md:text-[19px] font-bold text-[#2c3e50]"
                  itemProp="price"
                  content={item.price}
                >
                  {item.price}
                  <span itemProp="priceCurrency" content="GEL">
                    ₾
                  </span>
                </span>
                {isInSale(item.old_price, item.price) && (
                  <span className="text-[11px] md:text-[13px] text-[#7f8c8d] line-through">
                    {item.old_price}₾
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex gap-1 md:gap-2 justify-between items-center">
            <button
              onClick={handleCartClick}
              disabled={isOutOfStock}
              className={`group w-7 h-7 md:w-[42px] md:h-[42px] rounded-full flex items-center justify-center transition-all duration-200 shadow-[0_2px_8px_rgba(0,0,0,0.1)] flex-shrink-0 ${
                isOutOfStock
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'hover:scale-105 text-slate-600'
              }`}
            >
              <CustomCart
                className="text-slate-600 group-hover:text-slate-800 transition-colors duration-300"
                size={16}
              />
            </button>
            <button
              disabled={isOutOfStock}
              className={`flex-1 py-1.5 md:py-2.5 rounded-full font-semibold transition-all duration-200 text-[13px] md:text-[15px] ${
                isOutOfStock
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-citrus-red text-white hover:bg-red-600'
              }`}
            >
              ყიდვა
            </button>
          </div>
        </div>
      </div>
    </Link>
  )
}

export default ProductItem
